import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { LogIn, Clock, MapPin, Users, Sun, Sprout, Smartphone, KeyRound, ArrowLeft, RefreshCw } from 'lucide-react';

export default function LoginPage() {
  const brandLogo = '/omlogo.png';
  const [loading, setLoading] = useState(false);
  // 'password' | 'otp'
  const [loginMode, setLoginMode] = useState('password');
  // OTP flow step: 1 = enter identifier, 2 = enter OTP
  const [otpStep, setOtpStep] = useState(1);
  const [otpIdentifier, setOtpIdentifier] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const [formData, setFormData] = useState({ username: '', password: '' });
  const { login, loginWithOtp } = useAuth();
  const navigate = useNavigate();

  // ── Password login ────────────────────────────────────────────
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.username, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP flow ──────────────────────────────────────────────────
  const startResendCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    if (!otpIdentifier.trim()) {
      toast.error('Please enter your email or phone number');
      return;
    }
    setLoading(true);
    try {
      await authService.requestOtp(otpIdentifier.trim());
      toast.success('OTP sent! Check your email.');
      setOtpStep(2);
      startResendCooldown();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const response = await authService.verifyOtp(otpIdentifier.trim(), otpCode.trim());
      await loginWithOtp(response);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = () => {
    if (resendCooldown > 0) return;
    setOtpCode('');
    handleRequestOtp();
  };

  const resetOtpFlow = () => {
    setOtpStep(1);
    setOtpCode('');
    setResendCooldown(0);
  };

  const features = [
    { icon: Clock, title: 'Smart Attendance', desc: 'Easy check-in with location tracking' },
    { icon: MapPin, title: 'Farm Locations', desc: 'Multi-location workforce management' },
    { icon: Users, title: 'Team Management', desc: 'Complete employee lifecycle' },
    { icon: Sprout, title: 'Organic Growth', desc: 'Analytics for sustainable teams' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#eef7f2] via-white to-[#f4efe6] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 nature-dots" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1920&q=80)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#022b1b]/95 via-[#033923]/90 to-[#876a2d]/70" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur border border-white/15 flex items-center justify-center p-2">
                <img src={brandLogo} alt="Organic Mandya" className="h-full w-full object-contain" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Organic Mandya</h1>
                <p className="text-sm text-white/80 uppercase tracking-[0.24em]">HRMS Portal</p>
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4 leading-tight">
              Nurturing Our<br />
              <span className="text-[#f6d77a]">People & Purpose</span>
            </h2>
            <p className="text-lg text-white/90 max-w-md">
              Empowering our organic farming community with modern HR solutions.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20 hover:bg-white/20 transition-colors">
                <feature.icon className="w-8 h-8 text-[#f6d77a] mb-3" />
                <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-white/80">{feature.desc}</p>
              </div>
            ))}
          </div>
          <div className="absolute bottom-8 left-12 flex items-center gap-2 text-white/60 text-sm">
            <Sun className="w-4 h-4" />
            <span>Growing naturally since 2014</span>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-14 h-14 rounded-2xl bg-[#033923] flex items-center justify-center p-2 shadow-lg shadow-[#033923]/20">
              <img src={brandLogo} alt="Organic Mandya" className="h-full w-full object-contain" />
            </div>
            <div>
              <span className="text-xl font-bold text-[#033923]">Organic Mandya</span>
              <p className="text-xs uppercase tracking-[0.2em] text-[#6b7f76]">HRMS Portal</p>
            </div>
          </div>

          <Card className="border-[#d7e7de] shadow-xl shadow-[#033923]/8">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-[#033923]">Welcome Back</CardTitle>
              <CardDescription className="text-[#5d746a]">Sign in to access your dashboard</CardDescription>
            </CardHeader>

            {/* Mode tabs */}
            <div className="flex mx-6 mb-2 rounded-lg overflow-hidden border border-[#d7e7de]">
              {[
                { key: 'password', icon: KeyRound, label: 'Password' },
                { key: 'otp', icon: Smartphone, label: 'OTP' },
              ].map(({ key, icon: Icon, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setLoginMode(key); resetOtpFlow(); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                    loginMode === key ? 'bg-[#033923] text-white' : 'bg-white text-[#5d746a] hover:bg-[#f0f9f4]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            <CardContent className="pt-4">
              {/* ── Password login form ── */}
              {loginMode === 'password' && (
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-[#033923]">Username / Employee ID</Label>
                    <Input
                      id="username"
                      data-testid="login-username"
                      placeholder="Enter your username or employee ID"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="border-[#d1dfd7] focus:border-[#033923] focus:ring-[#033923]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-[#033923]">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      data-testid="login-password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="border-[#d1dfd7] focus:border-[#033923] focus:ring-[#033923]"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#033923] hover:bg-[#022b1b] text-white glow-primary"
                    data-testid="login-submit"
                    disabled={loading}
                  >
                    {loading ? <span className="spinner mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                    Sign In
                  </Button>
                </form>
              )}

              {/* ── OTP login form ── */}
              {loginMode === 'otp' && (
                <>
                  {otpStep === 1 && (
                    <form onSubmit={handleRequestOtp} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="otp-identifier" className="text-[#033923]">Email or Phone Number</Label>
                        <Input
                          id="otp-identifier"
                          data-testid="otp-identifier"
                          placeholder="you@example.com or 9876543210"
                          value={otpIdentifier}
                          onChange={(e) => setOtpIdentifier(e.target.value)}
                          className="border-[#d1dfd7] focus:border-[#033923] focus:ring-[#033923]"
                          required
                        />
                        <p className="text-xs text-[#6b7280]">
                          We'll send a 6-digit OTP to the email linked with your account.
                        </p>
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#033923] hover:bg-[#022b1b] text-white glow-primary"
                        data-testid="send-otp-btn"
                        disabled={loading}
                      >
                        {loading ? <span className="spinner mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />}
                        Send OTP
                      </Button>
                    </form>
                  )}

                  {otpStep === 2 && (
                    <form onSubmit={handleVerifyOtp} className="space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <button
                          type="button"
                          onClick={resetOtpFlow}
                          className="text-[#5d746a] hover:text-[#033923]"
                        >
                          <ArrowLeft className="w-4 h-4" />
                        </button>
                        <p className="text-sm text-[#5d746a]">
                          OTP sent to <strong className="text-[#033923]">{otpIdentifier}</strong>
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="otp-code" className="text-[#033923]">Enter 6-Digit OTP</Label>
                        <Input
                          id="otp-code"
                          data-testid="otp-code"
                          placeholder="123456"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="border-[#d1dfd7] focus:border-[#033923] focus:ring-[#033923] text-center text-2xl tracking-[0.5em] font-mono"
                          maxLength={6}
                          inputMode="numeric"
                          required
                          autoFocus
                        />
                        <p className="text-xs text-[#6b7280]">
                          Valid for 10 minutes. Check your spam folder if not received.
                        </p>
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-[#033923] hover:bg-[#022b1b] text-white glow-primary"
                        data-testid="verify-otp-btn"
                        disabled={loading || otpCode.length !== 6}
                      >
                        {loading ? <span className="spinner mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                        Verify & Sign In
                      </Button>
                      <div className="text-center">
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={resendCooldown > 0 || loading}
                          className="text-sm text-[#033923] hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 mx-auto"
                        >
                          <RefreshCw className="w-3 h-3" />
                          {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[#5d746a] mt-6">
            Organic Mandya {new Date().getFullYear()} - Pure. Natural. Honest.
          </p>
        </div>
      </div>
    </div>
  );
}
